import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useGridApiRef, type GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import { ListContainer, ListContent } from '~/components/PageLayout';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useAppContext } from '~/contexts';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { SpreadsheetyDataGrid, useDataSourceGridState, useSpreadsheetUtils } from '../lib';
import { DataSourceDataDocument, type DataSourceData__DataSource } from '../queries/data.generated';
import { AddRowDialog, Layout, SpreadsheetToolbar } from './components';

export const DataSourceData = () => {
  const { t } = useTranslation();
  const params = useParams<{ dataSourceId: string }>();
  const dataSourceId = parseInt(params.dataSourceId ?? '');

  const { hash } = useLocation();
  const highlightedId = useMemo(() => (hash ? Number(hash.split('#').at(1)) : undefined), [hash]);

  const [searchParams, setSearchParams] = useSearchParams();

  const { currentNetwork } = useAppContext();

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const search = searchParams.get('search') ?? '';

  // Preserve the previous dataSource during loading so columns/keys stay stable
  const dataSourceRef = useRef<DataSourceData__DataSource | undefined>(undefined);

  const { orderBy, setOrderByParams, sortModel } = useOrderByParams('dynamic');

  const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);

  const onSearch = (value: string) => {
    setSearchParams((p) => {
      value ? p.set('search', value) : p.delete('search');
      return p;
    });
  };

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setOrderByParams(model.at(0) ?? null);
    },
    [setOrderByParams],
  );

  const { data, loading } = useQuery(DataSourceDataDocument, {
    variables: {
      dataSourceId,
      networkId: currentNetwork.id,
      orderBy,
      orderFirst: highlightedId,
      page,
      perPage,
      search,
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.dataSource.items.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.dataSource.items.pageInfo.nodesCount);

  if (data?.network?.dataSource) {
    dataSourceRef.current = data.network.dataSource;
  }
  const dataSource = dataSourceRef.current;

  const apiRef = useGridApiRef();

  const {
    columnOrder,
    columnVisibility,
    columnWidths,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnWidthChange,
  } = useDataSourceGridState(dataSourceId);

  const handleColumnOrderChange = () => {
    const allColumns = apiRef.current.getAllColumns();
    const newOrder = allColumns.map((col) => col.field);
    onColumnOrderChange(newOrder);
  };

  const {
    cellModesModel,
    columns,
    confirmDeleteProps,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    highlightedIndex,
    processRowUpdate,
    rows,
    setCellModesModel,
  } = useSpreadsheetUtils(dataSource, highlightedId, {
    columnOrder,
    columnWidths,
    orderBy,
    orderFirst: highlightedId,
    search,
  });

  return (
    <Layout noPadding>
      <ListContainer>
        <ListContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <SpreadsheetToolbar
            editable={dataSource !== undefined && dataSource.kind !== 'INTEGRATION'}
            onAddRow={() => setAddRowDialogOpen(true)}
            onSearch={onSearch}
            search={search}
          />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <SpreadsheetyDataGrid
              autoHeight={false}
              cellModesModel={cellModesModel}
              columns={columns}
              columnVisibilityModel={columnVisibility}
              disableRowSelectionOnClick
              getRowClassName={({ row }) => (row.__enabled__ === 'false' ? 'disabled' : '')}
              hideFooter
              initialState={{ sorting: { sortModel } }}
              loading={loading}
              localeText={{
                noRowsLabel: t('pages.data_sources.edit.no_rows'),
              }}
              onCellEditStart={handleCellEditStart}
              onCellEditStop={handleCellEditStop}
              onCellKeyDown={handleCellKeyDown}
              onCellModesModelChange={setCellModesModel}
              onColumnOrderChange={handleColumnOrderChange}
              onColumnVisibilityModelChange={onColumnVisibilityChange}
              onColumnWidthChange={onColumnWidthChange}
              onSortModelChange={onSortModelChange}
              sortingMode="server"
              sortModel={loading ? [] : sortModel}
              pinnedColumns={{ left: ['id'], right: ['actions'] }}
              processRowUpdate={processRowUpdate}
              rowSelectionModel={highlightedIndex ? [highlightedIndex] : []}
              rows={rows}
              slotProps={{
                cell: { onFocus: handleCellFocus },
              }}
              sx={{ height: '100%' }}
            />
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <ListPagination totalCount={totalCount} />
          </Box>
          <ConfirmDialog
            {...confirmDeleteProps}
            confirm={t('pages.data_sources.edit.delete_row.confirm')}
            deleteConfirm
            prompt={t('pages.data_sources.edit.delete_row.prompt')}
            title={t('pages.data_sources.edit.delete_row.title')}
          />
          {dataSource && (
            <AddRowDialog
              dataSource={dataSource}
              onClose={() => setAddRowDialogOpen(false)}
              open={addRowDialogOpen}
            />
          )}
        </ListContent>
      </ListContainer>
    </Layout>
  );
};
